// ─────────────────────────────────────────────
//  native/urgencyAlarm.js — phone vibration for an incoming ⚡ urgency ping.
//
//  Native app: @capacitor/haptics via dynamic import behind the isNative()
//  guard (D-10 rule — the web bundle and vitest never execute native plugin
//  code). navigator.vibrate is unreliable inside the iOS/Android webview,
//  so the plugin is the path that makes vibration WORK once ATC ships as
//  an app (Phase 6).
//  Web: navigator.vibrate (Android browsers; iOS Safari has no vibration
//  API — the audible chirp from audio/chime.js still plays).
//
//  Fire-and-forget: every path is try/caught — a vibration failure must
//  never break the alert flow. The TONE lives in audio/chime.js
//  (playUrgencyAlarm) next to the emergency siren.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import { isNative } from "./platform";

// Patterns are ms [vibrate, pause, vibrate, …]. Emergency = long + insistent;
// urgency = shorter. Both run through the same native/web dispatch below.
const URGENCY_PATTERN = [300, 150, 300, 150, 400];
const EMERGENCY_PATTERN = [400, 200, 400, 200, 400, 200, 600];

async function vibratePattern(pattern) {
  try {
    if (isNative()) {
      const { Haptics } = await import("@capacitor/haptics");
      for (let i = 0; i < pattern.length; i += 2) {
        if (i) await new Promise((r) => setTimeout(r, pattern[i - 1]));
        await Haptics.vibrate({ duration: pattern[i] });
      }
      return true;
    }
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(pattern);
      return true;
    }
  } catch (e) {
    console.warn("[urgencyAlarm] vibration unavailable:", e);
  }
  return false;
}

export const vibrateUrgency = () => vibratePattern(URGENCY_PATTERN);
export const vibrateEmergency = () => vibratePattern(EMERGENCY_PATTERN);
